import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import {
  ChevronRight,
  GripVertical,
  Guardrails,
  Workflow
} from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import IconContainer from "@/components/ui/icon-container";
import Badge from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

export interface WorkflowItem {
  id: string;
  name: string;
  isGuardrails: boolean;
  numQuestions?: number;
}

interface Props {
  item: WorkflowItem;
  index: number;
  isDragDisabled?: boolean;
  isLast?: boolean;
}

const DraggableWorkflowItem: React.FC<Props> = ({
  item,
  index,
  isDragDisabled = false,
  isLast = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleWorkflowClick = () => {
    const currentPath = location.pathname;
    const searchParams = location.search;
    const newPath = `${currentPath}/${item.id}${searchParams}`;
    navigate(newPath);
  };

  const content = (provided: any, snapshot: any, isClone: boolean = false) => (
    <div
      ref={isClone ? null : provided.innerRef}
      {...provided.draggableProps}
      className={cn(
        "group w-full flex flex-col items-start justify-center cursor-pointer h-[76px]",
        "max-w-[800px] pl-1 pr-4 py-4 gap-4",
        "bg-surface-widget-card",
        !isLast && "border-b border-border-border1",
        "hover:bg-surface-hover-soft transition-colors duration-200",
        snapshot.isDragging && [
          "border-t border-b border-border-border1",
          "bg-surface-focus-selected",
          "shadow-[0px_12px_24px_-4px_rgba(0,0,0,0.20),0px_0px_2px_-2px_rgba(0,0,0,0.18)]",
          "relative z-50"
        ],
        "group"
      )}
      style={provided.draggableProps.style}
      onClick={handleWorkflowClick}
    >
      <div className="flex flex-row w-full justify-between items-center">
        <div className="flex flex-row items-center w-full gap-0">
          <div
            {...provided.dragHandleProps}
            className={cn(
              "px-0 cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              isDragDisabled && "pl-4"
            )}
          >
            {!isDragDisabled && (
              <IconContainer color="secondary">
                <GripVertical />
              </IconContainer>
            )}
          </div>
          <div className="flex w-full gap-4 items-center">
            <LogoIconContainer
              index={-1}
              icon={<Workflow />}
              className="size-10"
            />
            <div className="flex flex-col w-full justify-center">
              <Text variant="lgBody">{item.name}</Text>
              {item.isGuardrails && (
                <Badge
                  className="pl-0 border-none bg-transparent"
                  icon={
                    <IconContainer size="5" color="secondary">
                      <Guardrails />
                    </IconContainer>
                  }
                  textVariant="bodySecondary"
                  text={t(
                    "settings.deals.stageStudio.stageDetailedView.guardrails"
                  )}
                />
              )}
            </div>
          </div>
        </div>
        <IconButton
          variant="iconNoBg"
          className={cn(
            "opacity-0 transition-all duration-200 ease-in-out group-hover:opacity-100"
          )}
          size="lg"
          iconColor="primary"
        >
          <ChevronRight />
        </IconButton>
      </div>
    </div>
  );

  return (
    <Draggable
      draggableId={item.id}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <>
          {content(provided, snapshot)}
          {snapshot.isDragging &&
            createPortal(content(provided, snapshot, true), document.body)}
        </>
      )}
    </Draggable>
  );
};

export default DraggableWorkflowItem;
